import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';
import BlockContent from '@sanity/block-content-to-react';

import {
  RawPortableText,
  GatsbySanityImageFluid,
  GatsbySanityImageFluidNoBase64,
} from '../../types';
import WhoWeArePageLayout from '../../components/WhoWeArePageLayout';
import serializers from '../../serializers';
import Video from '../../components/Video';
import SEO from '../../components/SEO';

import * as styles from './rovensa.module.scss';

import RovensaLogoSvg from '../../assets/images/logos/ROVENSA_white_well-balanced-agriculture.svg';

// CROPPROTECTION
import ascenzaLogo from '../../assets/images/logos/organogram/CropProtection/ascenza.svg';
import selectisLogo from '../../assets/images/logos/organogram/CropProtection/selectis.fv.svg';
// BIONUTRITION
import microQuimicaLogo from '../../assets/images/logos/organogram/BioNutrition/microquimica.svg';
import ogtLogo from '../../assets/images/logos/organogram/BioNutrition/ogt.svg';
import rodelLogo from '../../assets/images/logos/organogram/BioNutrition/rodel.svg';
import sdpLogo from '../../assets/images/logos/organogram/BioNutrition/sdp.svg';
import tradecorpLogo from '../../assets/images/logos/organogram/BioNutrition/tradecorp.svg';
// BIOCONTROL
import agrichemBioLogo from '../../assets/images/logos/organogram/BioControl/bioAgrichem.svg';
import agroTecnologiaLogo from '../../assets/images/logos/organogram/BioControl/agroTecnologia.svg';
import bionaLogo from '../../assets/images/logos/organogram/BioControl/biona.svg';
import ecoProyectosLogo from '../../assets/images/logos/organogram/BioControl/ecoProyectos.svg';
import evenAgroLogo from '../../assets/images/logos/organogram/BioControl/evenAgro.svg';
import idaiNatureLogo from '../../assets/images/logos/organogram/BioControl/idaiNature.svg';

// OTHERS
import oroagriLogo from '../../assets/images/logos/organogram/oroagri.svg';

interface QueryData {
  sanityWhoWeAre: {
    rovensaSection: {
      image?: {
        asset?: {
          fluid: GatsbySanityImageFluidNoBase64;
        };
      };
      imageText: string;
      _rawIntroText: RawPortableText;
      organogramImage?: {
        asset?: {
          fluid: GatsbySanityImageFluid;
        };
      };
      presentationVideoUrl: string;
    };
  };
}

const rovensa = (): React.ReactElement => {
  const data: QueryData = useStaticQuery(
    graphql`
      {
        sanityWhoWeAre {
          rovensaSection {
            image {
              asset {
                fluid(maxWidth: 810, maxHeight: 540) {
                  ...GatsbySanityImageFluid_noBase64
                }
              }
            }
            imageText
            _rawIntroText(resolveReferences: { maxDepth: 4 })
            organogramImage {
              asset {
                fluid(maxWidth: 760) {
                  ...GatsbySanityImageFluid
                }
              }
            }
            presentationVideoUrl
          }
        }
      }
    `,
  );
  const rovensa = data.sanityWhoWeAre.rovensaSection || {};
  const { t } = useTranslation();

  return (
    <WhoWeArePageLayout currentSlug="rovensa">
      <SEO title={t('rovensa_page.title', 'ROVENSA')} urlPath={'/who-we-are/rovensa'} />
      {!!(rovensa.image && rovensa.image.asset && rovensa.imageText) && (
        <div className={styles.introSection}>
          <Img
            className={styles.introImage}
            loading="eager"
            fadeIn={false}
            fluid={rovensa.image.asset.fluid}
            alt=""
          />
          <div className={styles.introSmallDescription}>
            <h4 className={styles.smallDescription}>{rovensa.imageText}</h4>
            <div className={styles.logoContainer}>
              <img src={RovensaLogoSvg} className={styles.introLogo} alt="Rovensa Logo" />
            </div>
          </div>
        </div>
      )}
      {!!rovensa._rawIntroText && (
        <div className={styles.description}>
          <BlockContent blocks={rovensa._rawIntroText} serializers={serializers} />
        </div>
      )}
      <div className={styles.organogramWrapper}>
        <div className={styles.orgranogramGroupContainer}>
          <div className={styles.groupTitleContainer}>
            <h3 className={styles.groupTitle}>Crop Protection</h3>
          </div>
          <div className={styles.organogramLogoContainer}>
            <div className={styles.logoRow + ' ' + styles.cropProtectionGroup}>
              <div
                // href="https://www.ascenza.com/"
                // target="_blank"
                // rel="noopener noreferrer"
                className={styles.logo + ' ' + styles.ascenzaLogo}
              >
                <img src={ascenzaLogo} alt="" />
              </div>
              <div
                // href="https://www.selectis.pt/"
                // target="_blank"
                // rel="noopener noreferrer"
                className={styles.logo + ' ' + styles.selectisLogo}
              >
                <img src={selectisLogo} alt="" />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.orgranogramGroupContainer}>
          <div className={styles.groupTitleContainer}>
            <h3 className={styles.groupTitle}>Bionutrition</h3>
          </div>
          <div className={styles.organogramLogoContainer}>
            <div className={styles.logoRowsContainer + ' ' + styles.bioNutritionGroup}>
              <div className={styles.logoRow}>
                <div
                  // href="https://tradecorp.com.es/en/"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.tradecorpLogo}
                >
                  <img src={tradecorpLogo} alt="" />
                </div>
                <div
                  // href="https://ogt.ie/"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.ogtLogo}
                >
                  <img src={ogtLogo} alt="" />
                </div>
              </div>
              <div className={styles.logoRow}>
                <div
                  // href="http://www.rodelflowers.com/#home"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.rodelLogo}
                >
                  <img src={rodelLogo} alt="" />
                </div>
              </div>
            </div>
            <div className={styles.logoRowsContainer + ' ' + styles.bioNutritionGroup}>
              <div className={styles.logoRow}>
                <div
                  // href="https://microquimicatradecorp.com/"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.microQuimicaLogo}
                >
                  <img src={microQuimicaLogo} alt="" />
                </div>
                <div
                  // href="https://s-d-p.fr/"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.sdpLogo}
                >
                  <img src={sdpLogo} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/*


        */}
        <div className={styles.orgranogramGroupContainer}>
          <div className={styles.groupTitleContainer}>
            <h3 className={styles.groupTitle}>Biocontrol</h3>
          </div>
          <div className={styles.organogramLogoContainer}>
            <div className={styles.logoRowsContainer + ' ' + styles.bioControlGroup}>
              <div className={styles.logoRow}>
                <div
                  // href="https://www.idainature.com/pt-pt/"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.idaiNatureLogo}
                >
                  <img src={idaiNatureLogo} alt="" />
                </div>
                <div
                  // href="https://agrichembio.com/en/"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.agrichemBioLogo}
                >
                  <img src={agrichemBioLogo} alt="" />
                </div>
              </div>
            </div>
            <div className={styles.logoRowsContainer + ' ' + styles.bioControlGroup}>
              <div className={styles.logoRow}>
                <div
                  // href="https://www.oroagri.com/"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.oroagriLogo}
                >
                  <img src={oroagriLogo} alt="" />
                </div>
                <div
                  // href="http://www.agrotecnologia.net/en/index.asp"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className={styles.logo + ' ' + styles.agroTecnologiaLogo}
                >
                  <img src={agroTecnologiaLogo} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!!rovensa.presentationVideoUrl && (
        <Video className={styles.video} url={rovensa.presentationVideoUrl}></Video>
      )}
      <div className={styles.buttonWrapper}>
        <a
          className={styles.button}
          href={'https://www.rovensa.com/'}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('rovensa_page.more_about_button', 'More About ROVENSA')}
        </a>
      </div>
    </WhoWeArePageLayout>
  );
};

export default rovensa;
